// Customer Assist widget — the always-mounted floating UI that the
// AssistSessionProvider drives. Three visual modes:
//
//   1. Setup (phase='setup')      — centered modal Dialog. The agent
//                                    types `agent_purpose` and POSTs
//                                    to workflow 17.
//   2. Live, expanded             — floating bottom-right card with
//      (phase='live',                full chat surface (header,
//       minimized=false)             banners, scroller, compose row).
//   3. Live, minimized            — slim pill in the bottom-right
//      (phase='live',                showing ticket subject + status
//       minimized=true)              dot + unread badge. Click → expand.
//
//   4. Ended (phase='ended')       — small floating confirmation card,
//                                    auto-dismisses after a beat.
//
// All state lives in the AssistSessionProvider — the widget is purely
// a renderer. This means navigation between routes does NOT unmount
// the channel subscription; the agent keeps receiving messages and
// the desktop-notification layer keeps firing.

import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { toastFailure } from '@/lib/toast';
import {
  Sparkles,
  X,
  Copy,
  Clock,
  Check,
  KeyRound,
  Send,
  Paperclip,
  Loader2,
  Bell,
  BellOff,
  PartyPopper,
  ChevronUp,
  Minimize2,
  ArrowRight,
  Mail,
  Hourglass,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTicket } from '@/lib/queries';
import { stageAssistFollowup, buildFollowupTemplate } from '@/lib/assist-followup';
import { postDraftAssistFollowup } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useAuth } from '@/lib/auth-guard';
import { supabase } from '@/lib/supabase';
import { postAssistStart, type AssistStartResponse } from '@/lib/api';
import { useAssistSession } from '@/lib/assist-session-context';
import {
  uploadAssistAttachments,
  ASSIST_ATTACHMENT_MAX_PER_MESSAGE,
  formatBytes,
  extractClipboardFiles,
} from '@/lib/assist-attachments';
import { AssistChatBubble } from '@/components/AssistChatBubble';
import { EmojiPickerButton } from '@/components/EmojiPicker';
import { cn } from '@/lib/utils';
import type { AttachmentMeta } from '@/lib/use-assist-channel';

/** Live countdown to a session's `expires_at`. Ticks every second.
 *  The pg_cron expire job (migration 047) actually flips the status
 *  server-side; this hook just drives the visible UI so the agent
 *  knows how much wait time remains. `expired=true` lets the widget
 *  switch to a "timed out" presentation immediately when the local
 *  clock hits zero — the cron + workflow 18 catch up afterwards. */
function useExpiryCountdown(expiresAt: string | null) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!expiresAt) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [expiresAt]);

  if (!expiresAt) {
    return { remaining: 0, display: '', expired: false, warningSoon: false } as const;
  }
  const exp = new Date(expiresAt).getTime();
  const remaining = Math.max(0, Math.floor((exp - now) / 1000));
  const mm = Math.floor(remaining / 60);
  const ss = remaining % 60;
  const display =
    remaining === 0 ? 'Expired' : `${mm}:${String(ss).padStart(2, '0')}`;
  return {
    remaining,
    display,
    expired: remaining === 0,
    /** Under 5 min — used to switch the timer styling to a more
     *  urgent presentation. */
    warningSoon: remaining > 0 && remaining < 5 * 60,
  } as const;
}

export function CustomerAssistWidget() {
  const session = useAssistSession();

  if (session.phase === 'idle') return null;

  if (session.phase === 'setup') {
    return <SetupModal />;
  }

  if (session.phase === 'ended') {
    return <EndedToastCard />;
  }

  // Live phase — floating widget. Two render shapes (minimized vs
  // expanded) but both are anchored bottom-right.
  return session.minimized ? <MinimizedPill /> : <ExpandedFrame />;
}

// ── Setup modal ──────────────────────────────────────────────────────────────
function SetupModal() {
  const session = useAssistSession();
  const [purpose, setPurpose] = useState('');
  const [starting, setStarting] = useState(false);

  if (!session.bound) return null;

  async function submitStart() {
    if (!purpose.trim() || !session.bound) return;
    setStarting(true);
    try {
      const res = (await postAssistStart({
        ticket_id: session.bound.ticketId,
        agent_purpose: purpose.trim(),
      })) as AssistStartResponse;
      if (!res?.ok) {
        if (res?.error === 'session_already_active') {
          // Race with another tab/agent. Re-trigger openForTicket so we
          // pick up the existing session by querying the DB.
          toast.info('A session was already in flight — switching to it.');
          await session.openForTicket({
            ticketId: session.bound.ticketId,
            ticketSubject: session.bound.ticketSubject,
          });
          return;
        }
        throw new Error(res?.error || 'Failed to start session');
      }
      const magic_link = res.magic_link ?? `${window.location.origin}/assist/${res.token}`;
      session.setAgentPurpose(purpose.trim());
      session.attachLiveSession({
        sessionId: res.session_id!,
        token: res.token!,
        magicLink: magic_link,
        agentName: session.bound.agentName,
        expiresAt: res.expires_at ?? null,
      });
      toast.success('Magic link emailed to the client.');
    } catch (e) {
      toastFailure(e, "Couldn't start the assist session");
    } finally {
      setStarting(false);
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && session.cancelSetup()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-foreground/70" />
            Customer Assist session
          </DialogTitle>
          <DialogDescription>
            Open a live chat with the client to handle something time-sensitive — like a 2FA code that won't survive the email loop.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <div>
            <Label htmlFor="assist-purpose" className="text-xs">
              What do you need from the client?
            </Label>
            <Textarea
              id="assist-purpose"
              autoFocus
              rows={3}
              value={purpose}
              onChange={(e) => setPurpose(e.target.value)}
              placeholder='e.g. "Need the 2FA code from your POD authenticator so I can log in and fix the trigger."'
              className="mt-1 text-sm"
            />
            <p className="mt-1 text-2xs text-muted-foreground">
              The client sees this on the join screen so they know why you're asking for a live session.
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => session.cancelSetup()} disabled={starting}>
            Cancel
          </Button>
          <Button onClick={() => void submitStart()} disabled={starting || !purpose.trim()}>
            {starting ? 'Sending magic link…' : 'Start session + email client'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Minimized pill ───────────────────────────────────────────────────────────
function MinimizedPill() {
  const session = useAssistSession();
  if (!session.bound || !session.channel) return null;
  const ch = session.channel;
  const att = session.attention;
  const countdown = useExpiryCountdown(session.bound.expiresAt);

  const clientOnline = ch.peers.length > 0;
  // Unread = messages from the client since the agent last had the
  // widget expanded + tab focused. Owned by the attention hook so it
  // matches the desktop notification semantics exactly.
  const unread = att?.unreadCount ?? 0;
  // "Just joined" — client connected, agent hasn't dismissed the
  // banner yet. Glow the pill emerald so it's visually unmissable.
  const justJoined = att?.joinedBannerVisible === true;
  const timedOutNoJoin = countdown.expired && !clientOnline;

  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-0 z-50 flex justify-end p-3 sm:p-4">
      <button
        type="button"
        onClick={() => session.expand()}
        className={cn(
          'pointer-events-auto flex w-[320px] max-w-[calc(100vw-1.5rem)] items-center gap-2.5 rounded-full border bg-background px-3 py-2 text-left shadow-lg transition-all hover:shadow-xl',
          justJoined
            ? 'border-tint-success-fg ring-2 ring-tint-success-ring/60 animate-pulse'
            : unread > 0
              ? 'border-foreground/30'
              : 'border-border',
        )}
      >
        <span className="relative flex h-2.5 w-2.5 shrink-0">
          {clientOnline ? (
            <>
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-tint-success-fg opacity-60" />
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-tint-success-fg" />
            </>
          ) : (
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-muted-foreground/40" />
          )}
        </span>
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-medium text-foreground">
            {session.bound.ticketSubject || 'Customer Assist'}
          </div>
          <div className="truncate text-2xs text-muted-foreground">
            {timedOutNoJoin
              ? 'Session timed out — no client joined'
              : justJoined
                ? 'Client just joined — say hi'
                : clientOnline
                  ? 'Client connected'
                  : countdown.display
                    ? `Waiting for client · ${countdown.display}`
                    : 'Waiting for client…'}
          </div>
        </div>
        {unread > 0 && (
          <span className="flex h-5 min-w-[20px] shrink-0 items-center justify-center rounded-full bg-tint-critical-fg px-1.5 text-3xs font-semibold text-background">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
        <ChevronUp className="h-4 w-4 shrink-0 text-muted-foreground" />
      </button>
    </div>
  );
}

// ── Ended confirmation ───────────────────────────────────────────────────────
// Persistent card (no auto-dismiss) — sits in the bottom-right with
// follow-up actions so the agent can either jump back to the ticket
// or pop the reply composer pre-filled with a recap template. The
// agent now has to dismiss explicitly via × — the actions are too
// useful to vanish in 5 seconds.
function EndedToastCard() {
  const session = useAssistSession();
  const navigate = useNavigate();
  const ticketId = session.bound?.ticketId ?? null;
  const sessionId = session.bound?.sessionId ?? null;
  // Pull sender_name so the recap template can greet the client by
  // first name. Cached read — typically a no-op since the ticket
  // detail page already loaded it.
  const ticketQ = useTicket(ticketId ?? undefined);
  const senderName = ticketQ.data?.sender_name ?? null;
  const [aiDrafting, setAiDrafting] = useState(false);

  if (!session.bound || !ticketId) return null;

  function firstNameFrom(full: string | null): string | null {
    if (!full) return null;
    const first = full.trim().split(/\s+/)[0];
    return first || null;
  }

  function openTicket() {
    navigate(`/tickets/${ticketId}`);
    session.dismissEnded();
  }

  // Static-template path — instant, no Claude call. The body is a
  // generic "thanks for the live session" placeholder the agent
  // edits before sending. Lives next to the AI button so agents
  // who don't want to wait have a one-click escape hatch.
  function sendFollowupTemplate() {
    const body = buildFollowupTemplate({ clientFirstName: firstNameFrom(senderName) });
    stageAssistFollowup(ticketId!, body);
    navigate(`/tickets/${ticketId}`);
    session.dismissEnded();
  }

  // AI path — Claude reads the persisted transcript via the
  // draft-assist-followup Edge Function and writes a 1-2 paragraph
  // recap. Same human-in-the-loop guardrail as the existing AI Draft
  // button: the result lands in the Reply Composer for the agent to
  // edit + Send. CLAUDE.md rule 2 ("AI never auto-replies") holds.
  //
  // Fallback: if the API call fails (network, Claude down, etc.) we
  // fall through to the static template so the agent isn't blocked.
  // The error is surfaced via toast so they know what happened.
  async function sendFollowupAI() {
    if (!sessionId) {
      // Shouldn't happen — the EndedToastCard only renders when
      // phase==='ended', which only fires after we have a session_id.
      // Defensive fall-through to the static template.
      sendFollowupTemplate();
      return;
    }
    setAiDrafting(true);
    try {
      const res = await postDraftAssistFollowup(ticketId!, sessionId);
      if (!res.ok || !res.draft) {
        toast.error(`AI draft failed (${res.error ?? 'no text returned'}) — using template instead.`);
        sendFollowupTemplate();
        return;
      }
      // Stage the AI-generated body — same plumbing as the static
      // template path, so the Reply Composer hydrates it the same
      // way (sessionStorage prefill).
      stageAssistFollowup(ticketId!, res.draft);
      toast.success('AI draft ready — review and send.');
      navigate(`/tickets/${ticketId}`);
      session.dismissEnded();
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      toast.error(`AI draft failed (${msg}) — using template instead.`);
      sendFollowupTemplate();
    } finally {
      setAiDrafting(false);
    }
  }

  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-0 z-50 flex justify-end p-3 sm:p-4">
      <div className="pointer-events-auto flex w-[360px] max-w-[calc(100vw-1.5rem)] flex-col gap-2.5 rounded-md border border-border bg-background p-3 shadow-lg">
        <div className="flex items-start gap-2.5">
          <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-tint-success text-tint-success-fg">
            <Check className="h-3.5 w-3.5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-foreground">Session ended</div>
            <div className="mt-0.5 text-2xs text-muted-foreground">
              Transcript saved as an internal note on the ticket.
            </div>
          </div>
          <button
            type="button"
            onClick={() => session.dismissEnded()}
            className="text-muted-foreground hover:text-foreground"
            aria-label="Dismiss"
            title="Dismiss"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
        {/* Two-row action grid: AI follow-up sits prominently on its
            own row because it's the recommended path; the static
            template + open-ticket buttons are secondary actions.
            The AI button shows a spinner during the Claude call
            (typically 2–4 seconds for a short transcript). */}
        <div className="flex flex-col gap-1.5">
          <Button
            size="sm"
            onClick={() => void sendFollowupAI()}
            disabled={aiDrafting}
            className="h-8 w-full justify-center gap-1.5 text-xs"
          >
            {aiDrafting ? (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Drafting…
              </>
            ) : (
              <>
                <Sparkles className="h-3.5 w-3.5" />
                AI draft follow-up
              </>
            )}
          </Button>
          <div className="flex items-center gap-1.5">
            <Button
              size="sm"
              variant="outline"
              onClick={openTicket}
              disabled={aiDrafting}
              className="h-7 flex-1 justify-center gap-1.5 text-2xs"
            >
              <ArrowRight className="h-3 w-3" />
              Open ticket
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={sendFollowupTemplate}
              disabled={aiDrafting}
              className="h-7 flex-1 justify-center gap-1.5 text-2xs"
            >
              <Mail className="h-3 w-3" />
              Use template
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Expanded floating widget ─────────────────────────────────────────────────
function ExpandedFrame() {
  const session = useAssistSession();
  const { teamMember } = useAuth();
  if (!session.bound || !session.channel || !session.attention) return null;
  // Capture the narrowed values into locals so the inner callback
  // closures don't trip on TS's "could be null again" reasoning.
  const ch = session.channel;
  const att = session.attention;
  const { bound } = session;
  void (teamMember?.name ?? bound.agentName ?? 'You');

  const [draft, setDraft] = useState('');
  const [pendingAttachments, setPendingAttachments] = useState<AttachmentMeta[]>([]);
  const [uploading, setUploading] = useState(false);
  const [codeLabel, setCodeLabel] = useState('');
  const [requestingCode, setRequestingCode] = useState(false);
  const [ending, setEnding] = useState(false);
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const isNearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
    if (isNearBottom) el.scrollTop = el.scrollHeight;
  }, [ch.messages.length, ch.sensitiveReceived?.id]);

  const clientOnline = ch.peers.length > 0;
  // Sticky "client has been here at least once" flag. Once true, the
  // wall-clock countdown stops being meaningful — migration 055 makes
  // the server-side auto-expire skip sessions where `client_joined_at`
  // is set, so showing the timer ticking down would just confuse the
  // agent. We keep this sticky across disconnect/reconnect so a
  // briefly-flaky client doesn't bring the timer back.
  const [clientEverJoined, setClientEverJoined] = useState(false);
  useEffect(() => {
    if (clientOnline && !clientEverJoined) setClientEverJoined(true);
  }, [clientOnline, clientEverJoined]);

  const countdown = useExpiryCountdown(bound.expiresAt);
  // "Timed out without anyone arriving" — the specific case workflow
  // 18 will email the client about. When the local timer hits 0 AND
  // the client never joined, switch the widget to a friendlier
  // post-timeout layout instead of leaving a frozen chat surface
  // open. The server-side pg_cron + workflow-18 catch up shortly.
  // (If the client has EVER joined, this can't fire — the server
  // won't auto-expire and the countdown is hidden anyway.)
  const timedOutNoJoin = !clientEverJoined && countdown.expired && !clientOnline;
  // Don't visually freeze the chat as "expired" once the client has
  // joined — only the no-one-joined branch needs that disabled state.
  const disabled = ch.ended || ending || (countdown.expired && !clientEverJoined);

  // Shared upload pipeline — picker selection, drag/drop, and the
  // new clipboard paste path all call into this. Encapsulates the
  // room-remaining check + per-message cap + toast surface.
  async function uploadAndQueue(files: File[]) {
    if (!files || files.length === 0) return;
    const room = ASSIST_ATTACHMENT_MAX_PER_MESSAGE - pendingAttachments.length;
    if (room <= 0) {
      toast.error(`Max ${ASSIST_ATTACHMENT_MAX_PER_MESSAGE} files per message.`);
      return;
    }
    const toUpload = files.slice(0, room);
    if (files.length > toUpload.length) {
      toast.info(`Only the first ${room} file(s) will be attached.`);
    }
    setUploading(true);
    try {
      if (!bound.sessionId) return;
      const { attachments, failures } = await uploadAssistAttachments({
        client: supabase,
        sessionId: bound.sessionId,
        files: toUpload,
      });
      if (attachments.length > 0) setPendingAttachments((p) => [...p, ...attachments]);
      for (const f of failures) toast.error(`${f.filename}: ${f.reason}`);
    } finally {
      setUploading(false);
    }
  }

  async function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const fileList = e.target.files;
    if (!fileList || fileList.length === 0) return;
    const files = Array.from(fileList);
    e.target.value = '';
    await uploadAndQueue(files);
  }

  // Clipboard paste — Cmd-Shift-4 → Cmd-V flow for screenshots.
  // preventDefault so the textarea doesn't ALSO insert a literal
  // "image.png" placeholder string for the file. Plain-text paste
  // falls through to the default browser behaviour because
  // extractClipboardFiles returns [] in that case.
  async function handleTextareaPaste(e: React.ClipboardEvent<HTMLTextAreaElement>) {
    const files = extractClipboardFiles(e);
    if (files.length === 0) return;
    e.preventDefault();
    await uploadAndQueue(files);
  }

  // Emoji insertion at the textarea caret. Reads selectionStart /
  // End directly off the DOM so the inserted emoji lands where the
  // agent's cursor was, not at the end of the string (the naive
  // setDraft(draft + emoji) approach).
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  function insertEmoji(emoji: string) {
    const el = textareaRef.current;
    if (!el) {
      setDraft((d) => d + emoji);
      return;
    }
    const start = el.selectionStart ?? draft.length;
    const end = el.selectionEnd ?? start;
    const next = draft.slice(0, start) + emoji + draft.slice(end);
    setDraft(next);
    // Re-focus + place caret AFTER the inserted emoji on the next
    // frame so React has applied the value update first.
    requestAnimationFrame(() => {
      el.focus();
      const caret = start + emoji.length;
      el.setSelectionRange(caret, caret);
    });
  }

  async function send() {
    const t = draft.trim();
    if (!t && pendingAttachments.length === 0) return;
    try {
      await ch.sendChat({ text: t, attachments: pendingAttachments });
      setDraft('');
      setPendingAttachments([]);
    } catch (e) {
      toastFailure(e, "Couldn't send that message");
    }
  }

  async function requestCode() {
    try {
      await ch.sendCodeRequest(codeLabel.trim() || '2FA code');
      setCodeLabel('');
      setRequestingCode(false);
    } catch (e) {
      toastFailure(e, "Couldn't end the session");
    }
  }

  async function endNow() {
    // Starts the 10-second grace window. The provider broadcasts
    // `ending` to the client and schedules the real end. We don't
    // await — endSession returns once the broadcast lands; the
    // actual workflow-17 POST fires inside the timeout.
    setEnding(true);
    await session.endSession();
    setEnding(false);
  }

  async function endRightNow() {
    // Skips the grace window — used by the "End now" button shown
    // during the pending state, for agents who are 100% sure.
    setEnding(true);
    await session.endSessionNow();
    setEnding(false);
  }

  // Drive the countdown text in the pending-end footer. We compute
  // every 250ms so the displayed second ticks smoothly (a 1Hz tick
  // can land on the wrong second of the deadline if the timer drifts).
  const pendingEndAt = session.pendingEndAt;
  const [, forceTick] = useState(0);
  useEffect(() => {
    if (pendingEndAt === null) return;
    const id = setInterval(() => forceTick((n) => n + 1), 250);
    return () => clearInterval(id);
  }, [pendingEndAt]);
  const pendingSecondsLeft = pendingEndAt === null
    ? 0
    : Math.max(0, Math.ceil((pendingEndAt - Date.now()) / 1000));

  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-0 z-50 flex justify-end p-3 sm:p-4">
      <div
        className="pointer-events-auto flex w-[440px] max-w-[calc(100vw-1.5rem)] flex-col overflow-hidden rounded-lg border border-border bg-background shadow-xl"
        style={{ height: 'min(640px, calc(100vh - 96px))' }}
      >
        {/* Header — title + status + countdown + minimize */}
        <header className="flex shrink-0 items-center gap-2 border-b border-border/60 px-3 py-2.5">
          <Sparkles className="h-4 w-4 shrink-0 text-foreground/70" />
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-semibold leading-tight">
              {bound.ticketSubject || 'Customer Assist'}
            </div>
            <div className="mt-0.5 flex items-center gap-1.5 text-2xs text-muted-foreground">
              {timedOutNoJoin ? (
                <>
                  <Hourglass className="h-3 w-3 shrink-0 text-tint-critical-fg" />
                  <span className="text-tint-critical-fg">Session timed out — no client joined</span>
                </>
              ) : countdown.expired ? (
                <>
                  <Hourglass className="h-3 w-3 shrink-0 text-tint-critical-fg" />
                  <span className="text-tint-critical-fg">Session expired</span>
                </>
              ) : clientOnline ? (
                <>
                  <span className="relative inline-flex h-1.5 w-1.5">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-tint-success-fg opacity-60" />
                    <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-tint-success-fg" />
                  </span>
                  Client connected
                </>
              ) : (
                <>
                  <span className="inline-block h-1.5 w-1.5 rounded-full bg-muted-foreground/40" />
                  Waiting for client…
                </>
              )}
            </div>
          </div>
          {/* Countdown — visible ONLY while waiting for the client
              to arrive. Once the client joins, migration 055 disables
              the server-side auto-expire for this session, so a
              ticking timer would be misleading. We keep it neutral
              most of the time and switch to amber/rose in the last
              5 min while it's still relevant. */}
          {!clientEverJoined && countdown.display && !countdown.expired && (
            <span
              className={cn(
                'inline-flex shrink-0 items-center gap-1 rounded-md border px-1.5 py-0.5 font-mono text-2xs tabular-nums',
                countdown.warningSoon
                  ? 'border-tint-warning-ring bg-tint-warning text-tint-warning-fg'
                  : 'border-border/60 bg-muted/40 text-muted-foreground',
              )}
              title={`Session auto-ends at ${new Date(bound.expiresAt ?? '').toLocaleTimeString()}`}
            >
              <Hourglass className="h-3 w-3" />
              {countdown.display}
            </span>
          )}
          <button
            type="button"
            onClick={() => session.minimize()}
            className="rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground"
            aria-label="Minimize"
            title="Minimize (session keeps running)"
          >
            <Minimize2 className="h-3.5 w-3.5" />
          </button>
        </header>

        {/* Join banner — drops in on client connection */}
        {att.joinedBannerVisible && (
          <div className="shrink-0 border-b-2 border-tint-success-fg bg-gradient-to-r from-emerald-500 to-emerald-600 px-3 py-2.5 text-white animate-in slide-in-from-top fade-in duration-300">
            <div className="flex items-center gap-2.5">
              <PartyPopper className="h-4 w-4 shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold leading-tight">The client just joined</div>
                <div className="text-2xs opacity-90">Say hi.</div>
              </div>
              <Button
                size="sm"
                variant="ghost"
                onClick={att.dismissJoinedBanner}
                className="h-7 w-7 shrink-0 p-0 text-white hover:bg-white/20"
                aria-label="Dismiss"
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        )}

        {/* Notification permission prompts — only while still askable */}
        {att.permission === 'default' && !clientOnline && (
          <div className="shrink-0 border-b border-border/40 bg-muted/30 px-3 py-1.5">
            <div className="flex items-center gap-2">
              <Bell className="h-3 w-3 shrink-0 text-muted-foreground" />
              <span className="flex-1 text-3xs text-muted-foreground">
                Get a desktop ping when the client joins.
              </span>
              <Button
                size="sm"
                variant="outline"
                onClick={() => void att.enableNotifications()}
                className="h-6 gap-1 px-1.5 text-3xs"
              >
                Enable
              </Button>
            </div>
          </div>
        )}
        {att.permission === 'denied' && !clientOnline && (
          <div className="shrink-0 border-b border-border/40 bg-muted/20 px-3 py-1">
            <div className="flex items-center gap-1.5 text-3xs text-muted-foreground">
              <BellOff className="h-3 w-3" />
              Desktop alerts blocked — tab title still flashes.
            </div>
          </div>
        )}

        {/* Magic-link row — full while waiting, pill once connected */}
        {!clientOnline ? (
          <div className="shrink-0 border-b border-border/40 px-3 py-2">
            <div className="flex items-center justify-between gap-2 text-3xs text-muted-foreground">
              <span>Magic link emailed to the client</span>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  if (!bound.magicLink) return;
                  void navigator.clipboard.writeText(bound.magicLink).then(
                    () => toast.success('Link copied'),
                    () => toast.error('Could not copy'),
                  );
                }}
                className="h-6 gap-1 px-1.5 text-3xs"
              >
                <Copy className="h-2.5 w-2.5" />
                Copy
              </Button>
            </div>
            {bound.magicLink && (
              <code className="mt-1 block w-full truncate rounded border border-border/60 bg-muted/30 px-2 py-1 text-3xs text-muted-foreground">
                {bound.magicLink}
              </code>
            )}
          </div>
        ) : (
          <div className="shrink-0 border-b border-border/40 px-3 py-1">
            <button
              type="button"
              onClick={() => {
                if (!bound.magicLink) return;
                void navigator.clipboard.writeText(bound.magicLink).then(
                  () => toast.success('Link copied'),
                  () => toast.error('Could not copy'),
                );
              }}
              className="inline-flex items-center gap-1 text-3xs text-muted-foreground hover:text-foreground"
              title={bound.magicLink ?? undefined}
            >
              <Copy className="h-2.5 w-2.5" />
              Copy magic link
            </button>
          </div>
        )}

        {/* Chat scroller — swapped for a "timed out" message when the
            client never made it in. Server-side pg_cron (migration
            047) flips status='expired' within 5 min, and workflow 18
            sends the courtesy email within 10 min. */}
        {timedOutNoJoin ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-2 bg-muted/10 px-6 py-8 text-center">
            <Hourglass className="h-7 w-7 text-tint-critical-fg" />
            <div className="text-sm font-semibold">Session timed out</div>
            <p className="max-w-[260px] text-xs text-muted-foreground">
              The client wasn't able to join within 30 minutes. We'll automatically email them an invitation to reschedule — no further action needed.
            </p>
          </div>
        ) : (
          <div ref={scrollerRef} className="flex-1 overflow-y-auto bg-muted/10 px-3 py-2">
            {ch.messages.length === 0 ? (
              <p className="py-12 text-center text-xs text-muted-foreground">
                No messages yet — start typing below or request a code from the client.
              </p>
            ) : (
              <ul className="flex flex-col gap-1.5">
                {ch.messages.map((m) => (
                  <AssistChatBubble key={m.id} message={m} viewerRole="agent" />
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Sensitive received — amber stays semantic here */}
        {ch.sensitiveReceived && (
          <SensitiveBanner
            value={ch.sensitiveReceived.value}
            receivedAt={ch.sensitiveReceived.receivedAt}
          />
        )}

        {/* Code-request opener */}
        {requestingCode && (
          <div className="shrink-0 border-t border-border/60 bg-muted/30 px-3 py-2">
            <Label className="text-2xs font-medium text-foreground">
              What should the client see on the masked input?
            </Label>
            <div className="mt-1.5 flex items-center gap-1.5">
              <Input
                autoFocus
                value={codeLabel}
                onChange={(e) => setCodeLabel(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void requestCode();
                }}
                placeholder="e.g. POD 2FA code"
                className="h-8 text-sm"
              />
              <Button size="sm" onClick={() => void requestCode()} className="h-8 gap-1 px-2.5 text-xs">
                <KeyRound className="h-3.5 w-3.5" />
                Request
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setRequestingCode(false)} className="h-8 px-2">
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
            <p className="mt-1 text-3xs text-muted-foreground">
              Value transits encrypted via Realtime broadcast — never written to the database.
            </p>
          </div>
        )}

        {/* Pending attachment tray */}
        {pendingAttachments.length > 0 && (
          <div className="shrink-0 border-t border-border/40 bg-background/60 px-3 py-1.5">
            <div className="flex flex-wrap gap-1.5">
              {pendingAttachments.map((att, i) => (
                <div
                  key={`${att.url}-${i}`}
                  className="flex items-center gap-1.5 rounded-md border border-border/60 bg-muted/40 px-2 py-1 text-2xs"
                >
                  <Paperclip className="h-3 w-3 text-muted-foreground" />
                  <span className="max-w-[140px] truncate" title={att.filename}>{att.filename}</span>
                  <span className="text-3xs text-muted-foreground">{formatBytes(att.size)}</span>
                  <button
                    type="button"
                    onClick={() => setPendingAttachments((p) => p.filter((_, j) => j !== i))}
                    className="text-muted-foreground hover:text-foreground"
                    aria-label="Remove attachment"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Compose */}
        <div className="shrink-0 border-t border-border/60 bg-background px-3 py-2.5">
          <div className="flex items-end gap-1.5">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={disabled || uploading || pendingAttachments.length >= ASSIST_ATTACHMENT_MAX_PER_MESSAGE}
              title="Attach images or files"
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-border/60 text-muted-foreground hover:bg-accent disabled:opacity-50"
            >
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleFileSelect}
              className="hidden"
              accept="image/*,application/pdf,text/*,.doc,.docx,.xls,.xlsx,.csv,.zip"
            />
            <Textarea
              ref={textareaRef}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  void send();
                }
              }}
              onPaste={(e) => void handleTextareaPaste(e)}
              placeholder={clientOnline ? 'Type a message — paste screenshots with Cmd-V' : 'Waiting for the client — your messages queue here'}
              disabled={disabled}
              rows={1}
              className="max-h-28 min-h-9 flex-1 resize-none py-2 text-sm"
            />
            <EmojiPickerButton onPick={insertEmoji} disabled={disabled} />
            <Button
              size="sm"
              onClick={() => void send()}
              disabled={disabled || ch.sending || (!draft.trim() && pendingAttachments.length === 0)}
              className="h-9 gap-1 px-3"
            >
              {ch.sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
              Send
            </Button>
          </div>
          <div className="mt-2 flex items-center justify-between gap-2">
            {!requestingCode ? (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setRequestingCode(true)}
                disabled={disabled || !clientOnline}
                className="h-7 gap-1.5 px-2 text-xs"
                title={clientOnline ? 'Ask the client to type a 2FA code into a masked input' : 'Available once the client joins'}
              >
                <KeyRound className="h-3 w-3" />
                Request code
              </Button>
            ) : <div />}

            {timedOutNoJoin ? (
              // Timed out without a join — don't call End session
              // (that would flip status='ended' and skip the
              // courtesy email path). Just dismiss the widget
              // locally; the cron + workflow 18 finish up server-
              // side within ~10 min.
              <Button
                variant="outline"
                size="sm"
                onClick={() => session.dismissEnded()}
                className="h-7 gap-1.5 px-2 text-xs text-foreground/80"
              >
                <X className="h-3 w-3" />
                Dismiss
              </Button>
            ) : pendingEndAt !== null ? (
              // Pending end — show the countdown + Cancel + End now.
              // Cancel reverses the grace window (broadcasts
              // `end_cancel` to the client); End now skips the wait.
              <div className="flex items-center gap-1.5">
                <span className="inline-flex items-center gap-1 rounded-md border border-tint-warning-ring bg-tint-warning px-1.5 py-0.5 font-mono text-2xs tabular-nums text-tint-warning-fg">
                  <Clock className="h-3 w-3" />
                  Closing in {pendingSecondsLeft}s
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => session.cancelEnding()}
                  className="h-7 gap-1.5 px-2 text-xs"
                >
                  Cancel
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => void endRightNow()}
                  disabled={ending}
                  className="h-7 gap-1.5 px-2 text-xs text-tint-critical-fg hover:bg-tint-critical hover:text-tint-critical-fg"
                >
                  End now
                </Button>
              </div>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={() => void endNow()}
                disabled={ending || ch.ended}
                className="h-7 gap-1.5 px-2 text-xs text-foreground/80"
              >
                {ending ? (
                  <>
                    <Clock className="h-3 w-3 animate-spin" />
                    Saving…
                  </>
                ) : (
                  <>
                    <X className="h-3 w-3" />
                    End session
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function SensitiveBanner({ value, receivedAt }: { value: string; receivedAt: number }) {
  const TTL_MS = 60_000;
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const remaining = Math.max(0, Math.ceil((TTL_MS - (now - receivedAt)) / 1000));

  return (
    <div className="shrink-0 border-t-2 border-tint-warning-fg/80 bg-tint-warning px-3 py-2.5">
      <div className="flex items-center justify-between text-2xs font-semibold uppercase tracking-wide text-tint-warning-fg">
        <span className="flex items-center gap-1.5">
          <KeyRound className="h-3.5 w-3.5" />
          Code received
        </span>
        <span className="font-mono normal-case tracking-normal">{remaining}s</span>
      </div>
      <div className="mt-1.5 flex items-center gap-2">
        <code className="flex-1 select-all rounded border border-tint-warning-ring/60 bg-background px-3 py-2 text-base font-mono tracking-widest text-tint-warning-fg">
          {value}
        </code>
        <Button
          size="sm"
          variant="outline"
          onClick={() => {
            void navigator.clipboard.writeText(value).then(
              () => toast.success('Code copied'),
              () => toast.error('Could not copy'),
            );
          }}
          className="h-9 gap-1.5 border-tint-warning-ring px-2.5 text-xs text-tint-warning-fg hover:bg-tint-warning"
        >
          <Copy className="h-3 w-3" />
          Copy
        </Button>
      </div>
      <p className="mt-1 text-3xs text-tint-warning-fg/80">
        Not stored anywhere — vanishes when the timer runs out.
      </p>
    </div>
  );
}
